import React, { useEffect, useRef, useState } from 'react';
import { Award, Trophy } from 'lucide-react';
import { Combobox } from '../combobox';
import { injectTSDI } from '@core/tsdi';
import { Api } from '@core/api';
import { getCdnImage } from '@components/utils/cdn-image';
import type {
    BadgeTypeAdminDto,
    BadgeLevelAdminDto
} from '@core/api/FitnessActivityClient';

export interface BadgePickerOption {
    id: number;
    name: Record<string, string>;
    categoryName: Record<string, string>;
    levelIndex: number;
    badgeLevelId?: number;
    imageUrl?: string;
}

interface BadgePickerProps {
    value: BadgePickerOption | null;
    onChange: (badge: BadgePickerOption | null) => void;
    label?: string;
    disabled?: boolean;
}

const getLocalizedName = (name: Record<string, string> | undefined): string => {
    if (!name) return 'Unnamed';
    return name['en'] || name[Object.keys(name)[0]] || 'Unnamed';
};

const getLevelImageUrl = (level: BadgeLevelAdminDto): string | undefined => {
    if (level.image?.bucket && level.image?.key) {
        return getCdnImage(level.image.bucket, level.image.key);
    }
    return undefined;
};

export const BadgePicker: React.FC<BadgePickerProps> = ({
    value,
    onChange,
    label = 'Select Badge',
    disabled = false
}) => {
    const [types, setTypes] = useState<BadgeTypeAdminDto[]>([]);
    const [loadingTypes, setLoadingTypes] = useState(false);

    const [selectedType, setSelectedType] = useState<BadgeTypeAdminDto | null>(
        null
    );
    const [levels, setLevels] = useState<BadgeLevelAdminDto[]>([]);
    const [selectedLevel, setSelectedLevel] =
        useState<BadgeLevelAdminDto | null>(null);
    const [loadingLevels, setLoadingLevels] = useState(false);

    const { fitnessActivityClient } = injectTSDI(Api);
    const abortControllerRef = useRef<AbortController | null>(null);

    // Load badge types on mount
    useEffect(() => {
        loadBadgeTypes();
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    // Restore selected type/level from value prop
    useEffect(() => {
        if (value && types.length > 0 && !selectedType) {
            const type = types.find((t) => t.id === value.id);
            if (type) {
                setSelectedType(type);
            }
        }
    }, [value, types]);

    // Load levels when type changes
    useEffect(() => {
        if (selectedType) {
            loadLevelsForType(selectedType.id);
        } else {
            setLevels([]);
            setSelectedLevel(null);
        }
    }, [selectedType?.id]);

    // Restore selected level from value prop once levels load
    useEffect(() => {
        if (value && levels.length > 0 && !selectedLevel) {
            const level = levels.find((l) => l.levelIndex === value.levelIndex);
            if (level) {
                setSelectedLevel(level);
            }
        }
    }, [value, levels]);

    const loadBadgeTypes = async () => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setLoadingTypes(true);
        try {
            const response =
                await fitnessActivityClient.admin.badgeTypeAdminControllerGetTypes(
                    { size: 500 },
                    { signal: controller.signal }
                );

            if (response?.data?.content) {
                setTypes(response.data.content);
            }
        } catch (error) {
            if ((error as any)?.name !== 'AbortError') {
                console.error('Failed to load badge types:', error);
            }
        } finally {
            setLoadingTypes(false);
        }
    };

    const loadLevelsForType = async (typeId: number) => {
        setLoadingLevels(true);
        setLevels([]);
        setSelectedLevel(null);
        try {
            const response =
                await fitnessActivityClient.admin.badgeLevelAdminControllerGetLevels(
                    {
                        badgeTypeId: typeId,
                        size: 100,
                        sort: ['levelIndex']
                    }
                );

            const loadedLevels = response?.data?.content || [];
            setLevels(loadedLevels);

            // Auto-select if only one level
            if (loadedLevels.length === 1) {
                const level = loadedLevels[0];
                setSelectedLevel(level);
                emitChange(selectedType!, level);
            }
        } catch (error) {
            console.error('Failed to load badge levels:', error);
        } finally {
            setLoadingLevels(false);
        }
    };

    const emitChange = (type: BadgeTypeAdminDto, level: BadgeLevelAdminDto) => {
        onChange({
            id: type.id,
            name: level.name,
            categoryName: type.categoryName || {},
            levelIndex: level.levelIndex,
            badgeLevelId: level.id,
            imageUrl: getLevelImageUrl(level)
        });
    };

    const handleTypeChange = (type: BadgeTypeAdminDto | null) => {
        setSelectedType(type);
        setSelectedLevel(null);
        if (!type) {
            onChange(null);
        }
    };

    const handleLevelChange = (level: BadgeLevelAdminDto | null) => {
        setSelectedLevel(level);
        if (level && selectedType) {
            emitChange(selectedType, level);
        } else {
            onChange(null);
        }
    };

    const sortedTypes = [...types].sort((a, b) =>
        getLocalizedName(a.categoryName).localeCompare(
            getLocalizedName(b.categoryName)
        )
    );

    const typeValue =
        selectedType || (value ? types.find((t) => t.id === value.id) : null);

    return (
        <div className="flex flex-col gap-4">
            <Combobox
                label={label}
                placeholder="Select badge type"
                searchPlaceholder="Search badge types..."
                options={sortedTypes}
                value={typeValue || null}
                onChange={handleTypeChange}
                getOptionLabel={(option) => getLocalizedName(option.name)}
                getOptionKey={(option) => option.id}
                disabled={disabled}
                loading={loadingTypes}
                renderOption={(option) => (
                    <div className="flex w-full items-center gap-2">
                        <Trophy className="size-4 text-warning" />
                        <div className="grow">
                            <p className="text-sm">
                                {getLocalizedName(option.name)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {getLocalizedName(option.categoryName)}
                            </p>
                        </div>
                        {option.levelsCount > 0 && (
                            <span className="text-xs text-muted-foreground">
                                {option.levelsCount}{' '}
                                {option.levelsCount === 1 ? 'level' : 'levels'}
                            </span>
                        )}
                    </div>
                )}
            />

            {selectedType && levels.length > 1 && (
                <Combobox
                    label="Badge Level"
                    placeholder="Select level..."
                    searchPlaceholder="Search levels..."
                    options={levels}
                    value={selectedLevel}
                    onChange={handleLevelChange}
                    getOptionLabel={(option) => getLocalizedName(option.name)}
                    getOptionKey={(option) => option.id}
                    disabled={disabled || loadingLevels}
                    loading={loadingLevels}
                    renderOption={(option) => (
                        <div className="flex w-full items-center gap-2">
                            <Award className="size-4 text-info" />
                            <div className="grow">
                                <p className="text-sm">
                                    {getLocalizedName(option.name)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    Level {option.levelIndex}
                                </p>
                            </div>
                        </div>
                    )}
                />
            )}

            {selectedType && !loadingLevels && levels.length === 0 && (
                <p className="text-sm text-muted-foreground">
                    This badge type has no levels.
                </p>
            )}
        </div>
    );
};
